import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Endpoints } from 'src/app/core/resources/endpoints';

export type ValidationReason =
  | 'OK'
  | 'OUT_OF_BBOX'
  | 'OUT_OF_POLYGON'
  | 'MUNICIPALITY_NOT_FOUND'
  | 'INVALID_INPUT';

export interface LocationValidationRequest {
  cityId: string;
  lat: number;
  lng: number;
}

export interface LocationValidationResponse {
  valid: boolean;
  municipalityId?: string;
  municipalityName?: string;
  departmentName?: string;
  reason: ValidationReason;
  message: string;
  distanceToBoundaryMeters?: number;
}

export interface BoundingBox {
  minLng: number;
  minLat: number;
  maxLng: number;
  maxLat: number;
}

export interface MunicipalityPolygonData {
  id: string;
  mpioName: string;
  dptoName: string;
  dptoCode: string;
  bbox: BoundingBox;
  geometry: { type: 'Polygon' | 'MultiPolygon'; coordinates: any };
  areaKm2?: number;
  year?: number;
}

/**
 * Cliente del endpoint POST /catalog/cities/validate-location.
 *
 * - Maneja errores de red devolviendo un response "soft": valid=true para
 *   evitar bloquear al usuario por una caída del servicio (degradación
 *   graciosa). Esto se puede cambiar a valid=false si la política es
 *   "bloquear ante cualquier duda".
 * - Cachea por (cityId|lat|lng) la última request en memoria para no
 *   spamear el backend mientras el usuario edita el formulario.
 */
@Injectable({ providedIn: 'root' })
export class GeoValidationService {

  private inflight: { [key: string]: Observable<LocationValidationResponse> } = {};

  constructor(
    private http: HttpClient,
    private endpoints: Endpoints
  ) {}

  validate(req: LocationValidationRequest): Observable<LocationValidationResponse> {
    // El source decide si el backend lee de Firestore (default) o del geojson
    // local. Permite probar el endpoint en dev sin importar la colección.
    // Configurable vía environment.geoValidationSource = 'local' | 'firestore'.
    const source: string | undefined = (environment as any).geoValidationSource;
    const key = `${source || 'default'}|${req.cityId}|${req.lat.toFixed(6)}|${req.lng.toFixed(6)}`;
    if (!this.inflight[key]) {
      const url = environment.urlServerTeclogi + this.endpoints.cityValidateLocation;
      const params = source ? new HttpParams().set('source', source) : undefined;
      const t0 = performance.now();
      console.log('[GeoValidation] → POST', url, source ? `?source=${source}` : '(no source)', req);
      this.inflight[key] = this.http.post<LocationValidationResponse>(url, req, { params }).pipe(
        take(1),
        tap(resp => {
          const ms = (performance.now() - t0).toFixed(0);
          if (resp && resp.valid) {
            console.log(`[GeoValidation] ✓ ${ms}ms valid=true mpio=${resp.municipalityName}`);
          } else if (resp) {
            console.warn(`[GeoValidation] ✗ ${ms}ms valid=false reason=${resp.reason} mpio=${resp.municipalityName} dist=${resp.distanceToBoundaryMeters}m`);
          }
        }),
        catchError(err => {
          console.warn('[GeoValidation] error en backend, se acepta la dirección por degradación graciosa:', err);
          return of(<LocationValidationResponse>{
            valid: true,
            reason: 'OK',
            message: 'Servicio de validación no disponible. Se aceptó la dirección.'
          });
        }),
        shareReplay(1)
      );
    } else {
      console.log('[GeoValidation] cache HIT', key);
    }
    return this.inflight[key];
  }

  /**
   * Obtiene el polígono completo de un municipio por su DIVIPOLA.
   * Pensado para que el frontend lo dibuje en el mapa al elegir ciudad.
   * Cachea por cityId + source en memoria.
   */
  private polygonCache: { [key: string]: Observable<MunicipalityPolygonData | null> } = {};
  getMunicipalityPolygon(cityId: string): Observable<MunicipalityPolygonData | null> {
    if (!cityId) return of(null);
    const source: string | undefined = (environment as any).geoValidationSource;
    const key = `${source || 'default'}|${cityId}`;
    if (!this.polygonCache[key]) {
      const url = environment.urlServerTeclogi + 'catalog/cities/' + cityId + '/polygon';
      const params = source ? new HttpParams().set('source', source) : undefined;
      const t0 = performance.now();
      console.log('[GeoValidation] → GET polygon', url, source ? `?source=${source}` : '');
      this.polygonCache[key] = this.http.get<MunicipalityPolygonData>(url, { params }).pipe(
        take(1),
        tap(poly => {
          const ms = (performance.now() - t0).toFixed(0);
          if (poly) console.log(`[GeoValidation] ✓ polygon ${ms}ms ${poly.mpioName} (${poly.id})`);
          else console.warn(`[GeoValidation] polygon ${ms}ms <vacío>`);
        }),
        catchError(err => {
          console.warn('[GeoValidation] error obteniendo polígono', err);
          return of(null);
        }),
        shareReplay(1)
      );
    }
    return this.polygonCache[key];
  }

  /** Limpia el cache local: útil al cerrar el modal de tercero. */
  clearCache(): void {
    const n = Object.keys(this.inflight).length + Object.keys(this.polygonCache).length;
    if (n > 0) console.log(`[GeoValidation] clearCache (${n} entradas)`);
    this.inflight = {};
    this.polygonCache = {};
  }
}
